import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { createContext, type FormEvent, type ReactNode, useContext, useEffect, useState } from "react";
import { closeAll } from "@/data/live";
import { auth, USE_EMULATORS } from "@/lib/firebase";
import { isAllowed } from "@/model/auth";

const UserCtx = createContext<User | null>(null);
export const useUser = () => useContext(UserCtx)!;

export function doSignOut() {
  closeAll();
  return signOut(auth);
}

/** Nothing renders or subscribes until an allowlisted, verified admin is signed in. */
export function AuthGate({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        if (!u || !isAllowed(u)) closeAll();
        setUser(u);
      }),
    [],
  );

  if (user === undefined) return <div className="loading">Loading…</div>;
  if (user && isAllowed(user)) return <UserCtx.Provider value={user}>{children}</UserCtx.Provider>;

  const google = () =>
    signInWithPopup(auth, new GoogleAuthProvider()).catch((e: { code?: string; message: string }) =>
      setMsg(`Sign-in failed: ${e.code ?? e.message}`),
    );

  return (
    <div className="gate">
      <div className="gate-card">
        <div className="brand">
          <span className="logo">AI</span>
          <div style={{ minWidth: 0, textAlign: "left" }}>
            <b>AI Conference Twin</b>
            <span>Admin</span>
          </div>
        </div>
        <p data-testid="gate-msg">{msg ?? (user ? "Not authorised." : "Admin only.")}</p>
        {user ? (
          <button type="button" className="btn line" onClick={() => doSignOut()}>
            Sign out {user.email}
          </button>
        ) : (
          <>
            <button type="button" className="btn primary" onClick={google}>
              Sign in with Google
            </button>
            {USE_EMULATORS && <EmulatorSignIn onError={setMsg} />}
          </>
        )}
      </div>
    </div>
  );
}

/** Emulator builds only: email/password test accounts created by e2e/seed.ts. */
function EmulatorSignIn({ onError }: { onError: (m: string) => void }) {
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    signInWithEmailAndPassword(auth, String(f.get("email")), String(f.get("password"))).catch((x: { code?: string }) =>
      onError(`Sign-in failed: ${x.code ?? "error"}`),
    );
  };
  return (
    <form onSubmit={submit} aria-label="Emulator sign-in">
      <span className="label">Emulator test account</span>
      <input className="input" name="email" type="email" placeholder="Email" aria-label="Email" />
      <input className="input" name="password" type="password" placeholder="Password" aria-label="Password" />
      <button type="submit" className="btn line">
        Sign in (emulator)
      </button>
    </form>
  );
}
